import { useEffect, useState } from 'react';
import { DEFAULT_LATENESS_THRESHOLDS } from '../pages/Rides/carSprite';

/** Where each admin's own amber / red thresholds persist. */
export const LATENESS_STORAGE_KEY = 'trips-lateness-thresholds';

/**
 * A usable pair of thresholds, or null: whole-or-fractional minutes, amber at
 * or above 0, red strictly after amber — otherwise every car would be one
 * colour and the edit almost certainly a typo.
 */
export const validThresholds = (value) => {
  const atRisk = Number(value?.atRisk);
  const late = Number(value?.late);
  if (!Number.isFinite(atRisk) || !Number.isFinite(late) || atRisk < 0 || late <= atRisk) return null;
  return { atRisk, late };
};

const load = () => {
  try {
    return validThresholds(JSON.parse(localStorage.getItem(LATENESS_STORAGE_KEY))) || DEFAULT_LATENESS_THRESHOLDS;
  } catch {
    return DEFAULT_LATENESS_THRESHOLDS;
  }
};

// One value for the whole page — every car, the trip panel, the inputs — so an
// edit recolours everything at once rather than whichever component made it.
let current = load();
const listeners = new Set();

/**
 * Saves new thresholds for this admin, if they're valid. Returns whether they
 * were taken.
 */
export const setLatenessThresholds = (next) => {
  const valid = validThresholds(next);
  if (!valid) return false;
  current = valid;
  try {
    localStorage.setItem(LATENESS_STORAGE_KEY, JSON.stringify(valid));
  } catch {}
  listeners.forEach((listener) => listener(valid));
  return true;
};

/**
 * This admin's amber / red thresholds for late drivers (minutes late to the
 * first pick-up or for the return), and a setter. Persisted in localStorage,
 * so each person keeps their own; another tab's edit is picked up too.
 */
const useLatenessThresholds = () => {
  const [thresholds, setThresholds] = useState(current);

  useEffect(() => {
    listeners.add(setThresholds);
    setThresholds(current);
    const onStorage = (e) => {
      if (e.key !== LATENESS_STORAGE_KEY) return;
      current = load();
      listeners.forEach((listener) => listener(current));
    };
    window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(setThresholds);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return [thresholds, setLatenessThresholds];
};

export default useLatenessThresholds;
